import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { normalizeNodeResources } from "../utils/nodeContent.js";

const HIDDEN_STYLE = Object.freeze({
  position: "fixed",
  top: "-10000px",
  left: "-10000px",
  width: "min(280px, calc(100vw - 20px))",
  maxHeight: "calc(100vh - 20px)",
  visibility: "hidden",
  opacity: 0,
  pointerEvents: "none",
});

/** Shared description and source tooltip used by public concept maps and wheels. */
export default function PublicNodeDescriptionTooltip({ id, open, anchorElement, description, resources: rawResources }) {
  const [style, setStyle] = useState(HIDDEN_STYLE);
  const resources = normalizeNodeResources(rawResources);

  useEffect(() => {
    if (!open) {
      setStyle(HIDDEN_STYLE);
      return undefined;
    }

    let frame = 0;
    let secondFrame = 0;
    const positionTooltip = () => {
      const anchor = typeof anchorElement === "function" ? anchorElement() : anchorElement;
      const tooltip = document.getElementById(`node-description-${id}`);
      if (!anchor || !tooltip) return;
      const rect = anchor.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const padding = 10;
      if (!rect.width || !rect.height || !tooltipRect.width || !tooltipRect.height) return;

      let top = rect.top - tooltipRect.height - padding;
      let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
      if (top < padding) top = rect.bottom + padding;
      left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));
      top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding));
      setStyle({
        position: "fixed", top: `${top}px`, left: `${left}px`,
        color: "#30363b", backgroundColor: "#fcfbfa", border: "1px solid #dfe2e5",
        padding: "10px 12px", fontSize: "14px", whiteSpace: "normal",
        width: "min(280px, calc(100vw - 20px))", maxHeight: "calc(100vh - 20px)",
        overflowY: "auto", visibility: "visible", zIndex: 9999,
        boxShadow: "0 8px 24px rgba(23,24,26,0.09)", opacity: 1,
        transition: "opacity 0.2s ease", pointerEvents: "auto",
      });
    };

    frame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(positionTooltip);
    });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(positionTooltip);
    const anchor = typeof anchorElement === "function" ? anchorElement() : anchorElement;
    const tooltip = document.getElementById(`node-description-${id}`);
    if (anchor) observer?.observe(anchor);
    if (tooltip) observer?.observe(tooltip);
    window.addEventListener("resize", positionTooltip);
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(secondFrame);
      observer?.disconnect();
      window.removeEventListener("resize", positionTooltip);
    };
  }, [anchorElement, description, id, open, resources.length]);

  if (!open || (!description && resources.length === 0)) return null;
  return ReactDOM.createPortal(
    <div id={`node-description-${id}`} role="tooltip" style={style}>
      {description && <div>{description}</div>}
      {resources.length > 0 && <div style={{ marginTop: description ? 8 : 0, display: "grid", gap: 5 }}>
        {resources.map((resource) => <a key={resource.url} href={resource.url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} style={{ color: "#4566a7", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3, overflowWrap: "anywhere" }}>{resource.title}</a>)}
      </div>}
    </div>,
    document.body
  );
}
